import { computed, action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { emit, events } from '@estee/elc-service-bus';
import { LoyaltyRepository, offerRedemptionType } from '~repositories/LoyaltyRepository';
import { IBenefitsContent, IBenefitsInfo, IBenefitsLink } from '~views/dashboard/loyalty-benefits/ILoyaltyBenefits';
import { ILoyaltyRPCRequest } from '~api/interfaces/ILoyaltyRpcAPI';
import { IBirthDate } from '~exported/loyalty-birthday/ILoyaltyBirthday';
import Benefits from '~domain/entities/LoyaltyBenefitsInfo';
import { PriorityOrderSort, CategoryOrderSort } from '~utils/OffersSorting';

export interface IConfigParamsBenefits {
    benefits: IBenefitsContent;
}

class LoyaltyBenefitsController extends ViewController {
    @observable benefitOfferType: string;
    @observable birthdayOfferType: string;
    @observable isAddMyBirthdayModalOpen: boolean = false;
    @observable private configParams: IConfigParamsBenefits;

    private loyaltyRepository: LoyaltyRepository;

    constructor(loyaltyRepository: LoyaltyRepository) {
        super();
        this.loyaltyRepository = loyaltyRepository;
    }

    @action
    public setConfigParams = (configParams: IConfigParamsBenefits) => {
        this.configParams = configParams;
    };

    @action
    public loaded = async (benefitOfferType: string, birthdayOfferType?: string) => {
        this.isLoading = true;

        await this.loyaltyRepository.fetchBenefitsData(benefitOfferType);

        this.benefitOfferType = benefitOfferType;
        this.birthdayOfferType = birthdayOfferType || '';

        this.isLoading = false;
    };

    @action
    public handleRedeemOffer = async (applyOfferCode: ILoyaltyRPCRequest) => {
        this.isLoading = true;

        await this.loyaltyRepository.redeemOffer(
            applyOfferCode,
            this.benefitOfferType,
            offerRedemptionType.benefits
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.rewards;
        const offerSuccessMessageFromConfig = this.configParams?.benefits?.offerSuccessMessage?.text;
        if (
            offerSuccessMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'applied'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: offerSuccessMessageFromConfig
            });
        }

        const { offerCode } = applyOfferCode;
        const redeemOffer = this.loyaltyRepository?.benefitsOfferInfo?.inCartStatus;

        if (redeemOffer) {
            emit(events.LOYALTY_BENEFITS_REDEEM_CLICKED, offerCode);
        }

        this.isLoading = false;
    };

    @action
    public handleRemoveOffer = async (offerCode: string) => {
        this.isLoading = true;

        await this.loyaltyRepository.removeOffer(
            offerCode,
            this.benefitOfferType,
            offerRedemptionType.benefits
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.benefits;
        const offerRemovedMessageFromConfig = this.configParams?.benefits?.offerRemovedMessage?.text;
        if (
            offerRemovedMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'removed'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: offerRemovedMessageFromConfig
            });
        }

        emit(events.LOYALTY_BENEFITS_REMOVE_CLICKED, offerCode);

        this.isLoading = false;
    };

    @action
    public handleAddMyBirthday = async (birthday: IBirthDate) => {
        this.isLoading = true;

        await this.loyaltyRepository.setUserBirthdate(birthday);

        // Once birthday is updated, refresh the benefits and birthday offers available to the user
        await Promise.all([
            this.loyaltyRepository.fetchBirthdayData(this.birthdayOfferType),
            this.loyaltyRepository.fetchBenefitsData(this.benefitOfferType)
        ]);

        this.isLoading = false;
    };

    public onShopNowLinkClick = (linkLabel: string) => {
        emit(events.LOYALTY_SHOP_NOW_CLICKED, linkLabel);
    };

    public onAllBenefitsLinkClick = (linkData: IBenefitsLink) => {
        const { label } = linkData;

        emit(events.LOYALTY_ALL_BENEFITS_CLICKED, label);
    };

    @action
    public toggleAddMyBirthdayModal = () => {
        this.isAddMyBirthdayModalOpen = !this.isAddMyBirthdayModalOpen;
    };

    @computed
    public get data() {
        let benefitsInfo: IBenefitsInfo[] = [];
        if (this.configParams?.benefits.sorting?.enable && this.loyaltyRepository?.benefitsInfo?.benefitsDataFormatted) {
            const { priorityOrder, categoryOrder } = this.configParams.benefits.sorting;
            benefitsInfo = Benefits.getSortedBenefitsInfo(this.loyaltyRepository.benefitsInfo.benefitsDataFormatted,
                priorityOrder as PriorityOrderSort[], categoryOrder as CategoryOrderSort[]).benefitsDataFormatted;
        } else {
            benefitsInfo = this.loyaltyRepository?.benefitsInfo?.benefitsDataFormatted;
        }

        return {
            benefitsInfo,
            benefitsOfferInfo: this.loyaltyRepository?.benefitsOfferInfo,
            offerRedemptionMessages: this.loyaltyRepository?.offerRedemptionMessageData,
            isLoading: this.isLoading
        };
    }

    @computed
    public get configs() {
        return this.configParams;
    }

    @computed
    public get isBirthdateOnFile() {
        const {birthDay, birthMonth} = this.loyaltyRepository.loyaltyInfo;

        return (birthDay && birthMonth) ? true : false;
    }
}

export default LoyaltyBenefitsController;
